import type {
  IDocumentReaderUploadFile,
  IDocumentReaderUploadReference,
} from '@commenda/api/commenda-ai/document-reader/types';
import type { AttachmentItem, AttachmentUpload } from '@commenda/pathfinder';

import { DataRoomUploadHelper } from './DataRoomUploadHelper';
import { DataRoomFileState, type IDataRoomFileProgress } from './types';

/** A data room as Pathfinder Attachment pills: in the message box while it uploads, and on the message once sent. */
export class DataRoomAttachmentHelper {
  /** A sent room shows one pill per file up to this many files, and one pill for the room above it. */
  static readonly MAX_SENT_FILE_PILLS = 3;

  /** One uploading file as a pill: a ring with the share sent, red with its reason when it failed, and
   * its file icon (a zip for a .zip) once stored. */
  static toUploadingItem(row: Readonly<IDataRoomFileProgress>): AttachmentItem {
    const upload: AttachmentUpload =
      row.state === DataRoomFileState.STORED
        ? { status: 'ready' }
        : row.state === DataRoomFileState.FAILED
          ? { status: 'failed', error: row.error ?? 'Upload failed' }
          : {
              status: 'uploading',
              progress: DataRoomUploadHelper.percent(
                row.sentBytes,
                row.sizeBytes
              ),
            };
    return {
      id: row.fileId,
      filename: row.name,
      size: row.sizeBytes,
      kind: 'file',
      upload,
    };
  }

  /** The pills a sent message shows: one ready pill per file when the room is small and every one of
   * its files was read; otherwise, and until the files are read, one pill for the room. */
  static toSentItems(
    dataRoom: Readonly<IDocumentReaderUploadReference>,
    uploadFiles: readonly IDocumentReaderUploadFile[] | null
  ): AttachmentItem[] {
    if (
      uploadFiles &&
      DataRoomAttachmentHelper.hasFilePills(dataRoom) &&
      uploadFiles.length === dataRoom.fileCount
    )
      return uploadFiles.map((file) => ({
        id: file.id,
        filename: DataRoomAttachmentHelper.getSentFileName(file),
        size: file.sizeBytes,
        kind: 'file',
        upload: { status: 'ready' },
      }));
    const { fileCount } = dataRoom;
    return [
      {
        id: dataRoom.uploadId,
        filename: `Documents · ${fileCount.toLocaleString('en-US')} ${fileCount === 1 ? 'file' : 'files'}`,
        size: dataRoom.totalSizeBytes,
        kind: 'folder',
      },
    ];
  }

  /** True when the room is small enough to show a pill per file, which needs its file names read. */
  static hasFilePills(
    dataRoom: Readonly<IDocumentReaderUploadReference>
  ): boolean {
    return dataRoom.fileCount <= DataRoomAttachmentHelper.MAX_SENT_FILE_PILLS;
  }

  /** The name as chosen when the file came from a folder (its path keeps it); otherwise the stored name. */
  private static getSentFileName(
    file: Readonly<IDocumentReaderUploadFile>
  ): string {
    const pathName = file.relativePath?.split('/').pop();
    return pathName || file.fileName;
  }
}
